import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";
import Term from "@/components/Term";
import { useLang } from "@/context/LanguageContext";
import { ArrowLeft, Link2, Lock } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL_EN = {
  reflection: "Reflection",
  system: "Note",
  mission_complete: "Milestone",
  chapter_summary: "Chapter close",
  practice_completed: "Practice",
};
const TYPE_LABEL_HI = {
  reflection: "मनन",
  system: "टिप्पणी",
  mission_complete: "मील का पत्थर",
  chapter_summary: "अध्याय समापन",
  practice_completed: "अभ्यास",
};

export default function Journal() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const TYPE_LABEL = lang === "hi" ? TYPE_LABEL_HI : TYPE_LABEL_EN;
  const [entries, setEntries] = useState([]);
  const [personalizationCount, setPersonalizationCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/annals");
      setEntries(r.data.entries);
      setPersonalizationCount(r.data.personalization_count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePrivacy = async (entry) => {
    const next = !(entry.use_for_personalization ?? true);
    try {
      await api.patch(`/annals/${entry.entry_id}/privacy`, { use_for_personalization: next });
      setEntries(prev => prev.map(e => e.entry_id === entry.entry_id ? { ...e, use_for_personalization: next } : e));
      setPersonalizationCount(prev => next ? prev + 1 : prev - 1);
      toast.success(next ? "Used for personalization" : "Marked private");
    } catch {
      toast.error("Could not update.");
    }
  };

  const grouped = groupByChapter(entries);

  return (
    <div className="aged-paper min-h-screen -mx-6 -mt-6 px-6 pt-6 pb-24 animate-gentle-rise">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-body-sm mb-4" style={{ color: "var(--vedari-ink-soft)" }} data-testid="journal-back-btn">
        <ArrowLeft size={16} /> {t("nav.back")}
      </button>
      <h1 className="font-display text-[40px] leading-tight" data-testid="journal-title">{t("journal.title")}</h1>
      <p className="text-body mt-2" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("journal.your_journey")} · {t("journal.entries", { n: entries.length })}
      </p>

      {/* Why-to-write nudge — explicitly motivational */}
      <div className="vedari-card mt-5" style={{ borderLeft: "2px solid var(--vedari-gold)" }} data-testid="journal-intro-nudge">
        <p className="text-caption uppercase tracking-wider mb-2" style={{ color: "var(--vedari-gold)" }}>
          {lang === "hi" ? "हर प्रविष्टि मार्गदर्शन को तीक्ष्ण बनाती है" : "Every entry sharpens your guidance"}
        </p>
        <p className="text-body-sm font-serif italic leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
          {lang === "hi"
            ? "आप जितना अधिक यहाँ लिखेंगे — विचार, उलझनें, उत्साह, टिप्पणियाँ — आपके नक्षत्रों के साथ Vedari आपके लिए उतने ही विशिष्ट, उतने ही व्यक्तिगत सुझाव गढ़ सकेगा। एक छोटी प्रविष्टि भी अगले सप्ताह के मार्गदर्शन को आकार देती है।"
            : "The more you write here — feelings, challenges, small joys, observations from life — the more specific and personal Vedari's guidance becomes, drawn through your nakshatras. Even a short entry today shapes the suggestions you'll receive next week."}
        </p>
      </div>

      {entries.length > 0 && (
        <p className="text-caption mt-3 italic font-serif" style={{ color: "var(--vedari-ink-soft)" }} data-testid="journal-personalization-stat">
          {lang === "hi"
            ? <><KeyData>{personalizationCount}</KeyData> / <KeyData>{entries.length}</KeyData> प्रविष्टियाँ आपके मार्गदर्शन को व्यक्तिगत बना रही हैं।</>
            : <><KeyData>{personalizationCount}</KeyData> of <KeyData>{entries.length}</KeyData> entries are helping personalize your guidance.</>}
        </p>
      )}

      <div className="ornate-divider">
        <span className="text-xs tracking-[0.3em]">॥</span>
      </div>

      {loading && <div className="vedari-card animate-soft-pulse h-24" />}

      {!loading && entries.length === 0 && (
        <div className="text-center py-16">
          <p className="font-serif text-h2 mb-3">{t("journal.empty_title")}</p>
          <p className="text-body-sm font-serif italic max-w-xs mx-auto leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
            {t("journal.empty_body")}
          </p>
        </div>
      )}

      {grouped.map(([chapter, list]) => (
        <div key={chapter} className="mb-10" data-testid={`chapter-group-${chapter}`}>
          <div className="ornate-divider">
            <span className="font-serif text-body-sm tracking-wide" style={{ color: "var(--vedari-gold)" }}>
              {chapter !== "Your Journey" ? <><Term k="antardasha">{chapter}</Term></> : chapter}
            </span>
          </div>
          <div className="space-y-6">
            {list.map(e => {
              const usedForPersonalization = e.use_for_personalization ?? true;
              const isSubtle = e.type === "practice_completed" || e.type === "system";
              return (
                <article key={e.entry_id} className={`vedari-card relative ${isSubtle ? "py-3" : ""}`} data-testid={`journal-entry-${e.entry_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-caption"><KeyData>{formatDate(e.created_at)}</KeyData></p>
                    <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>
                      {TYPE_LABEL[e.type] || "Entry"}
                    </p>
                  </div>
                  {e.title && !isSubtle && <h3 className="font-serif text-h2 mb-2 leading-snug">{e.title}</h3>}
                  <p className={`${isSubtle ? "text-body-sm" : "text-body-lg"} leading-relaxed ${e.type !== "reflection" ? "font-serif italic" : ""}`} style={{ color: e.type !== "reflection" ? "var(--vedari-ink-soft)" : "var(--vedari-ink)" }}>
                    {e.content}
                  </p>
                  {/* Privacy toggle — only for personalization-eligible (user-written) entries */}
                  {!isSubtle && (
                    <button
                      onClick={() => togglePrivacy(e)}
                      className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full transition-all"
                      style={{
                        backgroundColor: usedForPersonalization ? "rgba(184,149,74,0.10)" : "rgba(107,101,96,0.10)",
                        color: usedForPersonalization ? "var(--vedari-gold)" : "var(--vedari-ink-soft)",
                      }}
                      title={usedForPersonalization ? "Used for personalization — tap to mark private" : "Private — tap to allow personalization"}
                      data-testid={`journal-privacy-toggle-${e.entry_id}`}
                    >
                      {usedForPersonalization ? <Link2 size={12} strokeWidth={1.75} /> : <Lock size={12} strokeWidth={1.75} />}
                      <span className="text-[10px] font-medium uppercase tracking-wider">
                        {usedForPersonalization ? "personalizing" : "private"}
                      </span>
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function groupByChapter(entries) {
  const map = new Map();
  for (const e of entries) {
    const key = e.chapter_name || "Your Journey";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  }
  return Array.from(map.entries());
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}
